import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCategories } from "@/hooks/useCategories";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import CategoryIcon from "@/components/common/CategoryIcon";
import { ChevronRight, Loader2, ArrowRight, Tag, CalendarClock } from "lucide-react";

function RootBadge({ type }) {
  if (type === "deal")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-100 text-[10px] font-bold px-1.5 py-0.5">
        <Tag className="w-3 h-3" /> Deal
      </span>
    );
  if (type === "event")
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-bold px-1.5 py-0.5">
        <CalendarClock className="w-3 h-3" /> Event
      </span>
    );
  return null;
}

/**
 * Iter-216 two-panel "Choose a Category" chooser (legacy parity).
 *
 * Routing is driven by each node's `type` field from the shared backend:
 *   - `type: "ad"`    → generic PostAdPage         (`/post-ad?category=<slug>`)
 *   - `type: "deal"`  → PostDealPage               (`/deals/post`)
 *   - `type: "event"` → PostEventPage              (`/events/post`)
 *
 * On EVERY route we forward the chosen `category_id` (root) and, when the
 * user picked a subcategory card, `subcategory_id` too. The post forms use
 * those ids on submit and render a "Category › Subcategory" title with a
 * "Change category" link back here.
 *
 * `useCategories` is a pure pass-through of `GET /api/classifieds/categories`
 * — the list is DB-driven, admin-managed and includes the new "Create Deal"
 * / "Create Event" roots the backend seeded for iter-216.
 */
export default function SellPickerPage() {
  const nav = useNavigate();
  const { data: rawCategories = [], isLoading } = useCategories();
  const [activeId, setActiveId] = useState(null);

  // Legacy parity (iter-218): the shared backend now returns "Create Deal" +
  // "Create Event" alphabetically among the A→Z ad categories, but the
  // legacy layout pins those two rows at the BOTTOM of the chooser so they
  // read as separate "Post something else" shortcuts rather than plain ad
  // categories. Do that split here, purely on `type`.
  const categories = useMemo(() => {
    const ads = rawCategories.filter((c) => (c.type || "ad") === "ad");
    const others = rawCategories.filter((c) => c.type === "deal" || c.type === "event");
    return [...ads, ...others];
  }, [rawCategories]);

  const active = categories.find((c) => c.id === activeId) || categories[0];

  const routeFor = (node) => {
    if (node?.type === "deal") return "/deals/post";
    if (node?.type === "event") return "/events/post";
    return `/post-ad?category=${encodeURIComponent(node?.slug || "")}`;
  };

  // Navigate with { category_id, subcategory_id } query params so the post
  // forms can echo them on submit. For deal/event roots the "category" is the
  // Create Deal / Create Event node itself; subcategory (when picked) is a
  // real business subcategory beneath it (e.g. "Beauty & Spa" for a deal).
  const go = (root, sub = null) => {
    const target = sub || root;
    const base = routeFor(target);
    const params = new URLSearchParams();
    if (root?.id) params.set("category_id", root.id);
    if (sub?.id) params.set("subcategory_id", sub.id);
    const sep = base.includes("?") ? "&" : "?";
    const url = params.toString() ? `${base}${sep}${params.toString()}` : base;
    nav(url);
  };

  const onRootClick = (c) => {
    // Nodes with no children are treated as one-tap shortcuts (e.g. Events
    // without subcategories) — jump straight to the post form.
    if (!c.children || c.children.length === 0) {
      go(c);
      return;
    }
    setActiveId(c.id);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="Choose a category" description="Pick a category to post your ad, deal, or event on Allsale Classified." />
      <Breadcrumbs items={[{ label: "Sell", to: "/sell" }, { label: "Choose a category" }]} />

      <div className="mt-6 mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">Choose a Category</h1>
        <p className="mt-2 text-slate-500">Pick where your ad belongs, then continue to fill out the details.</p>
      </div>

      {isLoading && (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      )}

      {!isLoading && (
        <div className="rounded-2xl bg-white shadow-card overflow-hidden grid grid-cols-1 md:grid-cols-[280px_1fr]">
          {/* Left rail — root categories */}
          <ul className="border-b md:border-b-0 md:border-r border-soft max-h-[70vh] overflow-y-auto" data-testid="sell-picker-roots">
            {categories.map((c, i) => {
              const isActive = active?.id === c.id;
              // Insert a hairline separator immediately BEFORE the first
              // pinned "Create Deal / Create Event" row so users see a clear
              // visual break between ad categories and the special
              // "Post something else" shortcuts.
              const prev = categories[i - 1];
              const showDivider = c.type !== "ad" && prev && (prev.type || "ad") === "ad";
              return (
                <React.Fragment key={c.id}>
                  {showDivider && (
                    <li
                      aria-hidden="true"
                      data-testid="sell-picker-divider"
                      className="border-t border-soft my-1"
                    />
                  )}
                  <li>
                  <button
                    type="button"
                    onClick={() => onRootClick(c)}
                    onMouseEnter={() => setActiveId(c.id)}
                    data-testid={`sell-picker-root-${c.slug}`}
                    className={`w-full flex items-center justify-between gap-2 px-4 py-3 text-left text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)]"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${isActive ? "bg-[color:var(--brand-blue)] text-white" : "bg-slate-100 text-slate-500"}`}>
                        <CategoryIcon name={c.icon} thumbnailUrl={c.thumbnail_url} emoji={c.emoji} alt={c.name} className="w-4 h-4" />
                      </span>
                      <span className="clamp-1">{c.name}</span>
                      <RootBadge type={c.type} />
                    </span>
                    <ChevronRight className={`w-4 h-4 shrink-0 ${isActive ? "text-[color:var(--brand-blue)]" : "text-slate-300"}`} />
                  </button>
                </li>
                </React.Fragment>
              );
            })}
          </ul>

          {/* Right panel — subcategories */}
          <div className="p-6 md:p-8" data-testid="sell-picker-subs">
            {active && (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
                    <CategoryIcon name={active.icon} thumbnailUrl={active.thumbnail_url} emoji={active.emoji} alt={active.name} className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                      {active.name}
                      <RootBadge type={active.type} />
                    </div>
                    <div className="text-xs text-slate-500">{(active.children || []).length} subcategories</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => go(active)}
                    data-testid={`sell-picker-continue-${active.slug}`}
                    className="ml-auto hidden md:inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-4 py-2 transition-colors"
                  >
                    Post in {active.name} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {(active.children || []).length > 0 ? (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {active.children.map((sc) => (
                      <li key={sc.id}>
                        <button
                          type="button"
                          onClick={() => go(active, sc)}
                          data-testid={`sell-picker-sub-${sc.slug}`}
                          className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-soft bg-white text-left text-sm font-semibold text-slate-700 hover:border-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-wash)]/50 hover:text-[color:var(--brand-blue)] transition-colors"
                        >
                          <span className="clamp-1">{sc.name}</span>
                          <ChevronRight className="w-4 h-4 text-slate-300" />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-slate-500 py-6">
                    No subcategories — <button onClick={() => go(active)} className="font-semibold text-[color:var(--brand-blue)] hover:underline">continue to post in {active.name}</button>.
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => go(active)}
                  className="md:hidden mt-6 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-4 py-2.5 transition-colors"
                >
                  Post in {active.name} <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 text-center text-sm text-slate-500">
        Prefer to skip? <Link to="/post-ad" className="font-semibold text-[color:var(--brand-blue)] hover:underline">Post without picking a category</Link>.
      </div>
    </div>
  );
}
